import React, { useContext, useRef, useState } from 'react'
import { Box, Button, MenuItem, Select, Typography, Dialog, DialogTitle, DialogContent, Stack, TextField } from '@mui/material';
import { useConfirmDialog } from './useConfirmDialog';
import { PostCountryMasterUploadCSV } from '../DataFetch/ManagementAPI';
import { useSuccessDialog } from './useSuccessDialog';
import { useErrorDialog } from './useErrorDialog';
import { FiscalYearListType, useInit } from '../DataFetch/useInit';
import { SessionContext } from '../ManagementRoot';

/**
 * 国マスタアップロードダイアログHook。
 * 年度とファイルを指定して、DBに国マスタを登録するダイアログを表示する。
 */
export function useCountryMasterUploadDialog() {

    const {init} = useInit();

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const [isComplete, setIsComplete] = useState<boolean>(false);

    const {waitConfirm, ConfirmDialog} = useConfirmDialog();

    const {openSuccessDialog, SuccessDialog} = useSuccessDialog();

    const {openErrorDialog, ErrorDialog} = useErrorDialog();

    const ConfirmSession = useContext(SessionContext);

    const uploadYearRef = useRef<HTMLInputElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    function OpenDialog() {
        setIsOpen(true);
        setIsComplete(false);
    }

    async function RegisterCountry() {

        if(!await ConfirmSession()) {
            return;
        }

        const uploadYear:string | undefined = uploadYearRef.current?.value
        const file:File | undefined = fileRef.current?.files?.[0]

        if(typeof file === "undefined") {
            return;
        }
        
        setIsOpen(false);  

        await waitConfirm("国マスタをアップロードします。よろしいですか？")
        .then(res => {
            res && PostCountryMasterUploadCSV({
                year:uploadYear,
                file: file,
            })
            .then(res => {
                if(res) {
                    openSuccessDialog();
                    setIsOpen(false);
                    setIsComplete(true);
                } else {
                    openErrorDialog();
                }
            })
        })
    }

    function UploadDialog() {

        return(
            <>
                <Dialog
                    open={isOpen}
                    onClose={() => setIsOpen(false)}
                >
                    <DialogTitle>
                        <Typography>
                            国マスタアップロード
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" m={2}>
                            CSVファイルから国マスタデータを取り込み、登録します。<br/>
                            指定した対象年度にデータが登録されている場合は、<br/>
                            今回取り込んだデータに置き換えられます。
                        </Typography>
                        <Stack spacing={2}>
                            <Box>対象年度</Box>
                            <Box>
                                <Select
                                    fullWidth
                                    defaultValue={init?.fiscalyear.toString()}
                                    inputRef={uploadYearRef}
                                >
                                    {
                                        init?.fiscal_year_list.map((value:FiscalYearListType, index:number) =>(
                                            <MenuItem key={index} value={value.target_year.toString()}>
                                                {value.target_year}
                                            </MenuItem>
                                        ))
                                    }
                                </Select>
                            </Box>
                            <Box>CSVファイル</Box>
                            <Box>                                    
                                <TextField 
                                    type="file" 
                                    fullWidth
                                    inputRef={fileRef}
                                    inputProps={{accept:".csv"}}
                                />
                            </Box>
                            <Box>
                                <Button variant="contained" onClick={RegisterCountry}> 
                                    登録
                                </Button>
                            </Box>
                        </Stack>
                    </DialogContent>
                </Dialog>

                <ConfirmDialog/>
                <SuccessDialog/>
                <ErrorDialog/>
            </>
        );
    }

    return {
        /**
         * 実行すると国マスタアップロードダイアログが表示される
         */
        openUploadDialog: OpenDialog,

        /**
         * ダイアログを表示する画面要素のどこかに配置する
         */
        UploadDialog: UploadDialog,

        /**
         * アップロード完了フラグ
         */
        isComplete: isComplete
    }
}